import React, { memo, useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroller";
import "./AgendaExploreGridView.css";
import AgendaItemCard from "./AgendaItemCard";
import AgendaEmptyState from "./AgendaEmptyState";

function getGridColumnCount() {
  if (typeof window === "undefined") return 3;
  const width = window.innerWidth;
  if (width >= 2600) return 6;
  if (width >= 2000) return 5;
  if (width >= 1600) return 4;
  if (width > 1080) return 3;
  if (width > 640) return 2;
  return 1;
}

function useGridColumns() {
  const [columns, setColumns] = useState(getGridColumnCount);

  useEffect(() => {
    const handleResize = () => {
      setColumns(getGridColumnCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return columns;
}

export function AgendaExploreShimmerCard() {
  return (
    <article className="agenda-explore-shimmer-card" aria-hidden="true">
      {/* Cover */}
      <div className="agenda-explore-shimmer-cover agenda-shimmer-block">
        <div className="agenda-explore-shimmer-badges-top">
          <div className="agenda-explore-shimmer-badges-left">
            <span className="agenda-explore-shimmer-tag agenda-shimmer-block" />
            <span className="agenda-explore-shimmer-tag-sm agenda-shimmer-block" />
          </div>
          <span className="agenda-explore-shimmer-tag-sm agenda-shimmer-block" />
        </div>
      </div>

      {/* Body */}
      <div className="agenda-explore-shimmer-body">
        {/* Title (2 lines) */}
        <span className="agenda-explore-shimmer-title-l1 agenda-shimmer-block" />
        <span className="agenda-explore-shimmer-title-l2 agenda-shimmer-block" />

        {/* Description (2 lines) */}
        <span className="agenda-explore-shimmer-desc-l1 agenda-shimmer-block" />
        <span className="agenda-explore-shimmer-desc-l2 agenda-shimmer-block" />

        {/* Doctor Row */}
        <div className="agenda-explore-shimmer-doctor">
          <span className="agenda-explore-shimmer-avatar agenda-shimmer-block" />
          <span className="agenda-explore-shimmer-host agenda-shimmer-block" />
          <span className="agenda-explore-shimmer-role agenda-shimmer-block" />
        </div>

        {/* Meta Chips */}
        <div className="agenda-explore-shimmer-meta-row">
          <span className="agenda-explore-shimmer-chip agenda-shimmer-block" style={{ width: "65px" }} />
          <span className="agenda-explore-shimmer-chip agenda-shimmer-block" style={{ width: "60px" }} />
          <span className="agenda-explore-shimmer-chip agenda-shimmer-block" style={{ width: "50px" }} />
          <span className="agenda-explore-shimmer-chip agenda-shimmer-block" style={{ width: "55px" }} />
        </div>

        {/* Footer */}
        <div className="agenda-explore-shimmer-footer">
          <span className="agenda-explore-shimmer-attendees agenda-shimmer-block" />
          <span className="agenda-explore-shimmer-btn agenda-shimmer-block" />
        </div>
      </div>
    </article>
  );
}

export function AgendaExploreGridShimmer({ count }) {
  const columns = useGridColumns();
  const defaultCount =
    columns === 6 ? 24 : columns === 5 ? 20 : columns === 4 ? 16 : columns === 3 ? 15 : columns === 2 ? 12 : 6;
  const shimmerCount = typeof count === "number" ? count : defaultCount;

  return (
    <div
      className="agenda-explore-grid agenda-explore-grid-shimmer"
      role="status"
      aria-label="Loading agendas"
    >
      {Array.from({ length: shimmerCount }, (_, index) => (
        <AgendaExploreShimmerCard key={index} />
      ))}
      <span className="visually-hidden">Loading agendas</span>
    </div>
  );
}

function AgendaExploreGridView({
  events = [],
  onSelectEvent,
  onEditEvent,
  selectedCategory = "all",
  isLoading = false,
  isFetching = false,
  isFetchingNextPage = false,
  hasMore = false,
  loadMore = () => { },
}) {
  const columns = useGridColumns();
  const initialShimmerCount =
    columns === 6 ? 24 : columns === 5 ? 20 : columns === 4 ? 16 : columns === 3 ? 15 : columns === 2 ? 12 : 6;
  const loaderShimmerCount = columns === 1 ? 2 : columns;

  const showShimmer =
    isLoading || (isFetching && !isFetchingNextPage && events.length === 0);

  if (showShimmer) {
    return <AgendaExploreGridShimmer count={initialShimmerCount} />;
  }

  if (!events.length) {
    return (
      <AgendaEmptyState
        variant="explore"
        category={selectedCategory}
      />
    );
  }

  return (
    <div className="agenda-explore-container">
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (!isFetchingNextPage && hasMore) {
            loadMore();
          }
        }}
        hasMore={Boolean(hasMore)}
        useWindow={true}
        threshold={350}
        loader={
          <div
            className="agenda-explore-grid mt-4"
            key="agenda-explore-grid-loader"
          >
            {Array.from({ length: loaderShimmerCount }).map((_, idx) => (
              <AgendaExploreShimmerCard key={`infinite-loader-${idx}`} />
            ))}
          </div>
        }
      >
        <div className="agenda-explore-grid">
          {events.map((item) => (
            <AgendaItemCard
              key={item.id}
              item={item}
              onSelect={onSelectEvent}
              onEdit={onEditEvent}
            />
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
}

export default memo(AgendaExploreGridView);
